/**
 * `useSession()` — the current auth state.
 *
 * One-shot fetch at mount; loading state surfaces as `null`. Components
 * that need a signed-in user wrap in `<RequireUser>` which redirects to
 * `/sign-in` on null.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SessionResponse, UpdatePreferencesRequest, User, UserPreferences } from '@eloquor/api-contract';
import { getSession, logout as apiLogout } from '../api/auth';
import { updatePreferences as apiUpdatePreferences } from '../api/preferences';
import { ApiCallError } from '../api/client';

interface SessionState {
  loading: boolean;
  session: SessionResponse | null;
  user: User | null;
  isDemo: boolean;
  isAdmin: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  updatePreferences: (patch: UpdatePreferencesRequest) => Promise<UserPreferences>;
}

const SessionCtx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SessionResponse | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const s = await getSession();
      setSession(s);
    } catch (err) {
      if (err instanceof ApiCallError && err.apiError.code === 'account_suspended') {
        setSession(null);
        if (typeof window !== 'undefined' && window.location.pathname !== '/suspended') {
          window.location.replace('/suspended');
        }
      } else if (err instanceof ApiCallError && err.status === 401) {
        setSession(null);
      } else {
        // Surface unexpected errors so the UI can show a real message.
        // Don't throw — break only the session hook, not the page.
        // eslint-disable-next-line no-console
        console.error('session refresh failed', err);
        setSession(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await apiLogout().catch(() => undefined);
    setSession(null);
  }, []);

  const updatePreferences = useCallback(async (patch: UpdatePreferencesRequest) => {
    const { preferences } = await apiUpdatePreferences(patch);
    setSession((prev) => (prev ? { ...prev, user: { ...prev.user, preferences } } : prev));
    return preferences;
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionState>(
    () => ({
      loading,
      session,
      user: session?.user ?? null,
      isDemo: Boolean(session?.isEphemeralDemo),
      isAdmin: session?.user?.role === 'admin',
      refresh,
      logout,
      updatePreferences,
    }),
    [loading, session, refresh, logout, updatePreferences]
  );

  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionCtx);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
